import type { ClipboardSnapshot } from './classifyPaste';

const IMAGE_EXTENSIONS: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
};

const snapshotFromItems = async (items: ClipboardItem[]): Promise<ClipboardSnapshot> => {
    let html = '';
    let text = '';
    let imageFile: File | null = null;

    for (const item of items) {
        if (!html && item.types.includes('text/html')) {
            html = await (await item.getType('text/html')).text();
        }
        if (!text && item.types.includes('text/plain')) {
            text = await (await item.getType('text/plain')).text();
        }

        const imageType = item.types.find((type) => type.startsWith('image/'));
        if (!imageFile && imageType) {
            const blob = await item.getType(imageType);
            // Clipboard blobs carry no filename; the upload path keys off the
            // MIME type, so any stable name works.
            const extension = IMAGE_EXTENSIONS[blob.type] ?? 'png';
            imageFile = new File([blob], `imagem-colada.${extension}`, { type: blob.type });
        }
    }

    return { html, text, imageFile };
};

/**
 * Reads the system clipboard for the context-menu paste, which has no
 * `ClipboardEvent` to inspect.
 *
 * Must be reached synchronously from the click handler — Safari ties
 * `clipboard.read()` to the user activation that started the task.
 *
 * Degrades in two steps: `read()` (full snapshot) → `readText()` (text only,
 * for browsers that gate the richer API) → `null` when the clipboard is
 * genuinely unreadable, which callers surface to the user.
 */
export const readSystemClipboard = async (): Promise<ClipboardSnapshot | null> => {
    const clipboard = navigator.clipboard;
    if (!clipboard) return null;

    if (typeof clipboard.read === 'function') {
        try {
            return await snapshotFromItems(await clipboard.read());
        } catch {
            // Permission denied for the rich read, or the API is stubbed out.
            // Fall through to the text-only path rather than giving up.
        }
    }

    if (typeof clipboard.readText === 'function') {
        try {
            return { html: '', text: await clipboard.readText(), imageFile: null };
        } catch {
            return null;
        }
    }

    return null;
};
